/**
 * The following functions need to use `null`  as this is part of the XML DOM API
 */

import { assertUnreachable, isDefined, Mutable } from '@evorait/pace-core';
import * as _ from 'lodash';
import moment from 'moment';
import { Checkpoint, CheckpointSelection } from '../types/checkpoint';
import { CheckpointDependencies, Checkpoints } from '../types/checkpoints';
import { ContentBlock, ContentId, initialContentBlockState } from '../types/content-block';
import { ContentBlocks } from '../types/content-blocks';
import { CONTENT_TYPE, ContentChild } from '../types/content-child';
import { ContentResults, DynamicContentResults } from '../types/content-results';
import { Copyright } from '../types/copyright';
import { CustomerIdentification } from '../types/customer-identification';
import {
    CustomerIdentificationInputField,
    initialCustomerIdentificationInputFieldState,
} from '../types/customer-identification-input-field';
import { DatePerformed } from '../types/date-performed';
import { DiscrepancyLog } from '../types/discrepancy-log';
import { initialFormFilters } from '../types/form-filter';
import { FormTypeFilters, initialFormTypeFilters } from '../types/form-type-filters';
import { FormsConstants } from '../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE, FunctionalElement } from '../types/functional-element';
import { FunctionalElements } from '../types/functional-elements';
import { Heading } from '../types/heading';
import { Headings } from '../types/headings';
import { Abbreviation, HintsForFilling } from '../types/hints-for-filling';
import { MaterialSerialNumber } from '../types/material-serial-number';
import {
    AstoriaOption,
    isAstoriaOption,
    Option,
    OPTION_FUNCTIONAL_ELEMENT_TYPE,
    OptionDependencies,
    OptionDependency,
} from '../types/option';
import { OrgData } from '../types/org-data';
import { RADIO_OPTION_SELECTED, RADIO_OPTION_TYPE, RadioOption } from '../types/radio-option';
import { RadioOptions } from '../types/radio-options';
import {
    REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION,
    ReplacementOrLastProcedureDate,
} from '../types/replacement-or-last-procedure-date';
import { Signature } from '../types/signature';
import { DEFICIENCY_SELECTION, MEASUREMENT_SELECTION, SystemCondition } from '../types/system-condition';
import { TypeFilter } from '../types/type-filter';
import { ValueFunctionalElement } from '../types/value-functional-element';
import { WorkstepsPerformed } from '../types/worksteps-performed';
import { AnswerUtilities } from './answer-utilities';
import { FormUtilities } from './form-utilities';
import { getXmlIdsFromFunctionalElement } from './get-xml-ids-from-functional-element';
import { getChildNodes, getFirstNode, getLastNode } from './xml-dom-utilities';
import getElementType = FormUtilities.getElementType;
import FORMTYPE = FormsConstants.FORMTYPE;

/**
 *  Internally assigned, unique ID for new content blocks to help addressing them.
 * Mae public to be able to reset it for tests.
 */
let xmlNodeIdCounter = 0;

// Arra y with xmlId which can occur multiple times in a xml document.
const xmlIdsWithMultipleOccurrences = ['CustomerData.MDate', 'SerialNo'];

/**
 *  Removes XML children from an XML tag. FIXED-NOTION tag content is retained, content of other child tags is removed.
 *
 * @param xml the XML string to be scrubbed
 * @returns the scrubbed XML string
 */
function scrubXmlTags(xml: string): string {
    let editedXml = xml;

    // norm alize the string, ie replace all whitespace with a single blank character. If we don't do this, newline
    // char acters would interfere with the next steps to replace XML tags
    editedXml = editedXml.replace(/\s+/gi, ' ');

    // remo ve FIXED-NOTION tags, but keep their content:
    editedXml = editedXml.replace(/<FIXED-NOTION>(.*?)<\/FIXED-NOTION>/gi, ' $1 ');

    // remo ve EMPHASIS tags, but keep their content:
    editedXml = editedXml.replace(/<EMPHASIS>(.*?)<\/EMPHASIS>/gi, ' $1 ');

    // now  remove all remaining HTML tags including content!
    // firs t find all the remaining HTML tags
    const xmlTagRegExp = new RegExp('<([^/].*?)>', 'gi');
    const htmlTags = [];
    let match = xmlTagRegExp.exec(editedXml);
    while (match !== null) {
        // we s plit on ' ' and take only the first element because the tag could contain attributes, which we are not interested in
        htmlTags.push(match[1].split(' ')[0]);
        match = xmlTagRegExp.exec(editedXml);
    }
    htmlTags.forEach((tag) => {
        editedXml = editedXml.replace(new RegExp(`<${tag}>.*?</${tag}>`), '');
    });

    // trim the string, ie. remove leading and trailing whitespaces
    editedXml = editedXml.trim();
    // remo ve multiple white spaces again (we could have introduced new ones during the replacing process)
    editedXml = editedXml.replace(/\s+/gi, ' ');

    // cons ole.info(`AstoriaAstoriaXmlUtilities: scrubXmlTags: \n${xml}\n${editedXml}\n`);
    return editedXml;
}

function hasPdfFormOutsideDateRange(pdfForms: Node[], fromDate: moment.Moment, tillDate: moment.Moment): boolean {
    const pdfFormOutsideDateRange = pdfForms.find((p) => {
        const modifiedDateString = (p as Element).getAttribute('MODIFIED') ?? '';
        const modified = moment(modifiedDateString);

        // Chec k if PDFFORM modified timestamp is outside the given date range
        return modified.isBefore(fromDate) || modified.isAfter(tillDate);
    });

    return typeof pdfFormOutsideDateRange !== 'undefined';
}

function getOptions(doc: XMLDocument): RadioOptions {
    const path = '//CHECKBTN/PDFFORM';
    const nodes = getChildNodes(doc, doc, path);
    const options: Mutable<RadioOptions> = {};

    nodes.forEach((node) => {
        const xmlId = node.getAttribute('TITLE');
        const optionType = (node.getAttribute('TYPE') || '') as RADIO_OPTION_TYPE;
        const selected = node.getAttribute('SELECTED') === RADIO_OPTION_SELECTED.YES;
        if (!isDefined(xmlId)) {
            return;
        }

        const optionsArray: RadioOption[] = options[xmlId] || [];
        optionsArray.push({
            xmlId,
            optionType: optionType,
            selected,
            selectedInMaster: selected,
            contentLoadedFromXml: selected,
            disabled: false,
        });
        options[xmlId] = optionsArray;
    });

    return options;
}

function getCustomerIdentificationFieldByXmlId(doc: XMLDocument, xmlId: string): CustomerIdentificationInputField {
    const valueNode = getFirstNode(doc, doc, `//PDFFORM[@TITLE='${xmlId}']`);
    let label;
    try {
        // we n eed to get the last EMPHASIS node before the PDFFORM as there might be multiple PDFFORM with labels
        // in E MPHASIS nodes before this one (CELLPARA left out for clarity):
        // <ROW >
        // <ROW >
        //   <E MPHASIS>Label 1</EMPHASIS>    <-- first EMPHASIS tag with label
        //   <P DFFORM>1</PDFFORM>
        //   <E MPHASIS>Label 2</EMPHASIS>    <-- second EMPHASIS tag with "our" label
        //   <P DFFORM>2</PDFFORM>            <-- current PDFFORM
        // </RO W>
        const labelNode = getLastNode(
            doc,
            doc,
            `//PDFFORM[@TITLE='${xmlId}']/ancestor::ENTRY/preceding-sibling::ENTRY/CELLPARA/EMPHASIS`,
        );
        label = _.get(labelNode, 'textContent', '') ?? '';
    } catch (err) {
        label = '';
    }

    // find all input fields, and then get the order number
    const rows = getChildNodes(doc, doc, `//FMP-CONTENT/PROT-CONTENT//PDFFORM[@TYPE='EDIT']`);
    const order = rows.findIndex((row) => row.getAttribute('TITLE') === xmlId);

    return {
        ...initialCustomerIdentificationInputFieldState,
        xmlId: xmlId,
        value: _.get(valueNode, 'textContent') || '',
        label,
        order,
    };
}

function getOrgData(doc: XMLDocument): OrgData {
    const productPath = '//ORGDATA/PRODUCT';
    const productNode = getFirstNode(doc, doc, productPath);
    if (!isDefined(productNode)) {
        throw new Error(`AstoriaXmlUtilities: getOrgData: Could not find node for product path '${productPath}'!`);
    }
    const product = productNode.textContent ?? '';

    const productTypePath = '//ORGDATA/PRODTYPE';
    const productTypeNode = getFirstNode(doc, doc, productTypePath);
    if (!isDefined(productTypeNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getOrgData: Could not find node for product type path '${productTypePath}'!`,
        );
    }
    const productType = productTypeNode.textContent ?? '';

    const variantPath = '//ORGDATA/VARIANTE';
    const variantNode = getFirstNode(doc, doc, variantPath);
    if (!isDefined(variantNode)) {
        throw new Error(`AstoriaXmlUtilities: getOrgData: Could not find node for variant path '${variantPath}'!`);
    }
    const variant = variantNode.textContent ?? '';

    const productKeyPath = '//ORGDATA/PRODUCT-KEY';
    const productKeyNode = getFirstNode(doc, doc, productKeyPath);
    if (!isDefined(productKeyNode)) {
        throw new Error(`AstoriaXmlUtilities: getOrgData: Could not find node for variant path '${productKeyNode}'!`);
    }
    const productKey = productKeyNode.textContent ?? '';

    const doctypeKeyPath = '//ORGDATA/DOCTYPE-KEY';
    const doctypeKeyNode = getFirstNode(doc, doc, doctypeKeyPath);
    if (!isDefined(doctypeKeyNode)) {
        throw new Error(`AstoriaXmlUtilities: getOrgData: Could not find node for variant path '${doctypeKeyNode}'!`);
    }
    const doctypeKey = doctypeKeyNode.textContent ?? '';

    const modalityPath = '//ORGDATA/MODALITY';
    const modalityNode = getFirstNode(doc, doc, modalityPath);
    if (!isDefined(modalityNode)) {
        throw new Error(`AstoriaXmlUtilities: getOrgData: Could not find node for variant path '${modalityNode}'!`);
    }
    const modality = modalityNode.textContent ?? '';

    const defaultInfoClassPath = '/MED-DOC/@INFOCLASS';
    const defaultInfoClassNode = getFirstNode(doc, doc, defaultInfoClassPath);
    if (!isDefined(defaultInfoClassNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getOrgData: Could not find node for variant path '${defaultInfoClassNode}'!`,
        );
    }
    const defaultInfoClass = defaultInfoClassNode.textContent ?? '';

    const versionPath = '//ORGDATA/MODALITY';
    const versionNode = getFirstNode(doc, doc, versionPath);
    if (!isDefined(versionNode)) {
        throw new Error(`AstoriaXmlUtilities: getOrgData: Could not find node for variant path '${versionNode}'!`);
    }
    const version = versionNode.textContent ?? '';
    return {
        product,
        productType,
        variant,
        productKey,
        doctypeKey,
        modality,
        defaultInfoClass,
        version,
    };
}

function getCopyright(doc: XMLDocument): Copyright {
    const line1Path = '//COPYRIGHT/C-LINE1';
    const line1Node = getFirstNode(doc, doc, line1Path);
    if (!isDefined(line1Node)) {
        throw new Error(`AstoriaXmlUtilities: getCopyright: Could not find node for line1 path '${line1Path}'!`);
    }
    const line1 = line1Node.textContent ?? '';

    const line2Path = '//COPYRIGHT/C-LINE2';
    const line2Node = getFirstNode(doc, doc, line2Path);
    if (!isDefined(line2Node)) {
        throw new Error(`AstoriaXmlUtilities: getCopyright: Could not find node for path '${line2Path}'!`);
    }
    const line2 = line2Node.textContent ?? '';

    return {
        line1,
        line2,
    };
}

function getInstructions(doc: XMLDocument): string {
    const path = '//CHSHEET/ADD-USE';
    const node = getLastNode(doc, doc, path);
    if (!isDefined(node)) {
        throw new Error(`AstoriaXmlUtilities: getInstructions: Could not find node for path '${path}'!`);
    }

    return node.textContent ?? '';
}

function getInfoClass(doc: XMLDocument): FormsConstants.INFOCLASS {
    const path = '//MED-DOC';
    const node = getFirstNode(doc, doc, path);
    // For some Astoria Forms the info-class is missing
    // the info-class is being used for the document-feedback button and this should not be enabled for Astoria forms.
    if (!isDefined(node)) {
        return FormsConstants.INFOCLASS.UNKNOWN;
    }

    const infoClassAttribute = node.getAttribute('INFOCLASS') ?? '';
    const infoClass = Object.values(FormsConstants.INFOCLASS).find((value) => value === infoClassAttribute);

    return infoClass ?? FormsConstants.INFOCLASS.UNKNOWN;
}

function getSystemConditionMeasurements(
    doc: XMLDocument,
): Pick<SystemCondition, 'initialMeasurementLabel' | 'repeatMeasurementLabel' | 'measurementXmlId'> {
    const measurementTableIndex = 1;
    const initialMeasurementRadioIndex = 0;
    const initialMeasurementIndex = 1;
    const repeatMeasurementIndex = 3;

    const measurementsEntriesPath = `//PROTOCOL/SECTION[1]/SECTION[1]/TABLE[${measurementTableIndex}]//ROW//ENTRY`;
    const measurementsRowEntries = getChildNodes(doc, doc, measurementsEntriesPath);

    const initialMeasurementLabel =
        getFirstNode(doc, measurementsRowEntries[initialMeasurementIndex], './/CELLPARA/EMPHASIS')?.textContent ??
        undefined;
    const repeatMeasurementLabel =
        getFirstNode(doc, measurementsRowEntries[repeatMeasurementIndex], './/CELLPARA/EMPHASIS')?.textContent ??
        undefined;
    const measurementXmlId =
        getFirstNode(doc, measurementsRowEntries[initialMeasurementRadioIndex], './/PDFFORM')?.getAttribute('TITLE') ??
        undefined;

    return {
        initialMeasurementLabel,
        repeatMeasurementLabel,
        measurementXmlId,
    };
}

/**
 *  Takes an XML doctype definition, search for the entry of a specified entity and clones that entity definition
 *  while replacing the existing entity with the new entity given
 *
 * @param doctype the XML doctype definition
 * @param entity the entity to be replaced
 * @param newCseEntity the new entity for the CSE
 * @param newCustomerEntity the new entity for the customer
 * @returns the updated doctype definition
 * @throws Error if the entity definition could not be found
 */
function addAdditionalEntityToDoctype(
    doctype: string,
    entity: string,
    newCseEntity: string,
    newCustomerEntity: string,
): string {
    const entityRegex = new RegExp(`<!ENTITY ${entity} SYSTEM .*? NDATA\\s+eps>`, 'i');
    const entityMatch = doctype.match(entityRegex);
    if (!entityMatch || entityMatch.length !== 1) {
        throw new Error(
            `XmlParserService: addAdditionalEntityToDoctype: Could not find entity definition for entity '${entity}'`,
        );
    }

    // Crea te new entity definition by replacing the existing entity string with an extended entity string
    const newCseEntityDefinition = entityMatch[0].replace(new RegExp(entity, 'ig'), newCseEntity);
    const newCustomerEntityDefinition = entityMatch[0].replace(new RegExp(entity, 'ig'), newCustomerEntity);

    // Upda te doctype string by using search & replace for the old entity definition
    return doctype.replace(
        entityMatch[0],
        `${entityMatch[0]} ${newCseEntityDefinition} ${newCustomerEntityDefinition}`,
    );
}

/**
 *  Updates the signatures in the system condition XML with an updated entity for the signature image, based on a
 *  given signature object. The given signature already needs to contain the updated entity name.
 *
 * @param doc the XML document to be updated
 * @param signature the signature object containing the updated entity name
 * @throws Error if the signature element could not be found
 */
function updateSystemConditionSignatureEntity(doc: XMLDocument, signature: Signature): void {
    // Iden tify signature element based on given signature
    const figureNode = getFirstNode(doc, doc, `//PDFFORM[@TITLE='${signature.nameXmlId}']/preceding::FIGURE[1]`);
    if (typeof figureNode === null) {
        throw new Error(
            `AstoriaXmlUtilities: updateSystemConditionSignatureEntity: Could not find FIGURE element for signature type '${signature.type}'; searching with PDFFORM xmlId '${signature.nameXmlId}'`,
        );
    }

    // Upda te entity where it is used in there
    figureNode?.setAttribute('ENTITY', signature.entity);
}

function getHintsForFilling(doc: XMLDocument): HintsForFilling | undefined {
    const heading = scrubXmlTags(getFirstNode(doc, doc, '//PROTOCOL/SECTION[1]/SECTION[2]/HEADING')?.textContent ?? '');
    const description = scrubXmlTags(
        getChildNodes(doc, doc, '//PROTOCOL/SECTION[1]/SECTION[2]/PARA')
            .map((node) => node.textContent ?? '')
            .join(' '),
    );
    const abbreviationsHeading = scrubXmlTags(
        getFirstNode(doc, doc, '//PROTOCOL/SECTION[1]/SECTION[2]/SECTION[1]/HEADING')?.textContent ?? '',
    );
    const abbreviationsHead: Abbreviation = {
        abbreviation:
            getFirstNode(doc, doc, '//PROTOCOL/SECTION[1]/SECTION[2]/SECTION[1]//THEAD/ROW/ENTRY[1]')?.textContent ??
            '',
        explanation:
            getFirstNode(doc, doc, '//PROTOCOL/SECTION[1]/SECTION[2]/SECTION[1]//THEAD/ROW/ENTRY[2]')?.textContent ??
            '',
    };
    const firstAbbreviationIndex = 0;
    const firstExplanationIndex = 1;
    const secondAbbreviationIndex = 3;
    const secondExplanationIndex = 4;
    const abbreviations = getChildNodes(doc, doc, '//PROTOCOL/SECTION[1]/SECTION[2]/SECTION[1]//TBODY/ROW')
        .map((row) => {
            const cellparas = getChildNodes(doc, row, './/CELLPARA');
            const firstAbbreviation: Abbreviation = {
                abbreviation: cellparas[firstAbbreviationIndex].textContent ?? '',
                explanation: cellparas[firstExplanationIndex].textContent ?? '',
            };
            const secondAbbreviation: Abbreviation = {
                abbreviation: cellparas[secondAbbreviationIndex].textContent ?? '',
                explanation: cellparas[secondExplanationIndex].textContent ?? '',
            };
            return [firstAbbreviation, secondAbbreviation];
        })
        .reduce((accu, i) => {
            accu.push(...i);
            return accu;
        }, [] as Abbreviation[]);
    return { description, heading, abbreviationsHeading, abbreviationsHead, abbreviations };
}

function getHeading(doc: XMLDocument, node: Node, parentId: ContentId): Heading | undefined {
    const headingPath = 'HEADING';
    const headingNode = getFirstNode(doc, node, headingPath);
    let heading = '';
    if (headingNode !== null) {
        // Chec k if tag contains FIXED-NOTION tags and remove them
        // Samp le: <HEADING EXPANDABLE="NO" translate="yes"><FIXED-NOTION>CT</FIXED-NOTION>Technology Overview</HEADING>
        heading = scrubXmlTags(headingNode.innerHTML);
    }

    const chapterNumberPath = 'SETCHAPNUM';
    const chapterNumberNode = getFirstNode(doc, node, chapterNumberPath);
    const chapterNumber = !isDefined(chapterNumberNode) ? '' : (chapterNumberNode.textContent ?? '');

    if (heading && chapterNumber) {
        return {
            id: xmlNodeIdCounter++, // refe renced by ContentEntry::id
            chapterNumber,
            heading,
            parentId,
        };
    }

    return undefined;
}

function getCheckpoint(doc: XMLDocument, node: Node, parentId: ContentId): Checkpoint | undefined {
    const checkpointNode = getFirstNode(doc, node, 'CHECKPNT');

    // Chec k if this is a check point definition
    if (!isDefined(checkpointNode)) {
        return undefined;
    }

    // Get  text out of CHECKPNT node
    const checkpointText = scrubXmlTags(checkpointNode.innerHTML);

    // from now on assume that we have both a CHECKPNT and CHECKBTN node; the latter with PDFFORM children for the options
    const xmlId = doc.evaluate(
        'string(CHECKBTN/PDFFORM/@TITLE)',
        node,
        null,
        XPathResult.STRING_TYPE,
        null,
    ).stringValue;

    const elementTypeValue = doc.evaluate(
        'string(CHECKPNT/@TYPE)',
        node,
        null,
        XPathResult.STRING_TYPE,
        null,
    ).stringValue;
    if (elementTypeValue.length === 0) {
        throw new Error(`AstoriaXmlUtilities: getCheckpoint: checkpoint '${xmlId}' has no type attribute set!`);
    }

    const elementType = getElementType(elementTypeValue);

    return {
        id: xmlNodeIdCounter++,
        xmlId,
        label: checkpointText,
        elementType,
        parentId,
        controllingOption: undefined,
    };
}

function getDiscrepancyLog(doc: XMLDocument, node: Node, parentId: ContentId): DiscrepancyLog {
    const inputField = getFirstNode(doc, node, 'descendant::PDFFORM');
    if (!isDefined(inputField)) {
        throw new Error(
            `AstoriaXmlUtilities: getDiscrepancyLog: Could not identify PDFFORM in functional element '${parentId}'`,
        );
    }

    // Try  to find controlling (=directly preceding) checkpoint
    const checkpointXmlIdPath = './preceding-sibling::*[1][self::LOP-ENTRY]/CHECKBTN/PDFFORM/@TITLE';
    const controllingCheckpoint =
        doc.evaluate(checkpointXmlIdPath, node, null, XPathResult.STRING_TYPE, null).stringValue ?? '';

    const value = inputField.textContent ?? '';

    const xmlId = inputField.getAttribute('TITLE') ?? '';

    return {
        id: xmlNodeIdCounter++,
        parentId,
        type: FUNCTIONAL_ELEMENT_TYPE.DISCREPANCY_LOG,
        label: 'Discrepancy Log',
        xmlId,
        value,
        controllingCheckpoint,
        contentLoadedFromXml: value.length > 0,
        disabled: false,
        controllingOption: undefined,
    };
}

function getDatePerformed(doc: XMLDocument, node: Node, parentId: ContentId): DatePerformed {
    const childNodes = getChildNodes(doc, node, 'descendant::PDFFORM');
    if (!isDefined(childNodes)) {
        throw new Error(
            `AstoriaXmlUtilities: getDatePerformed: Could not identify PDFFORMs in functional element '${parentId}'`,
        );
    }

    let day = NaN;
    let dayXmlId = '';
    let month = NaN;
    let monthXmlId = '';
    let year = NaN;
    let yearXmlId = '';

    childNodes.forEach((childNode) => {
        const value = childNode.textContent ?? '';
        const xmlId = childNode.getAttribute('TITLE') ?? '';
        const radixParameter = 10;

        if (xmlId.indexOf('System.Startup.Date.d-') !== -1) {
            day = parseInt(value, radixParameter);
            dayXmlId = xmlId;
        } else if (xmlId.indexOf('System.Startup.Date.m-') !== -1) {
            // beca use the month is returned by the first 3 Characters(eg Jan), we need to get the moment month number.
            if (value.length > 0) {
                // We n eed the month number to be able to create correctly the moment date.
                const monthNumber = moment().month(value).format('MM');

                month = parseInt(monthNumber, radixParameter) - 1;
            }
            monthXmlId = xmlId;
        } else if (xmlId.indexOf('System.Startup.Date.y-') !== -1) {
            year = parseInt(value, radixParameter);
            yearXmlId = xmlId;
        }
    });

    const date = moment([year, month, day]).isValid() ? moment([year, month, day]).toISOString() : '';

    const dateLabelNode = getFirstNode(doc, node, './/TABTITLE');
    if (!isDefined(dateLabelNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getDatePerformed: Could not identify label for date PDFFORM in functional element '${parentId}'`,
        );
    }
    const dateLabel = dateLabelNode.textContent ?? '';

    // iden tify controlling checkpoint
    const checkpointXmlIdPath = './preceding-sibling::*[1][self::LOP-ENTRY]/CHECKBTN/PDFFORM/@TITLE';
    const controllingCheckpoint =
        doc.evaluate(checkpointXmlIdPath, node, null, XPathResult.STRING_TYPE, null).stringValue ?? '';

    return {
        id: xmlNodeIdCounter++,
        dateLabel,
        date,
        dayXmlId,
        monthXmlId,
        yearXmlId,
        parentId,
        type: FUNCTIONAL_ELEMENT_TYPE.DATE_PERFORMED,
        controllingCheckpoint,
        contentLoadedFromXml: date.length > 0,
        disabled: false,
        controllingOption: undefined,
    };
}

function getReplacementOrLastProcedureDate(
    doc: XMLDocument,
    node: Node,
    parentId: ContentId,
    type: FUNCTIONAL_ELEMENT_TYPE.LAST_PROCEDURE_DATE | FUNCTIONAL_ELEMENT_TYPE.REPLACEMENT_DATE,
): ReplacementOrLastProcedureDate {
    const entryNodes = getChildNodes(doc, node, './/ENTRY');
    if (!isDefined(entryNodes)) {
        throw new Error(
            `AstoriaXmlUtilities: getReplacementOrLastProcedureDate: Could not identify ENTRY tags in functional element '${parentId}'`,
        );
    }

    // the  XML structure for replacement date is not too nice, but fortunately the order of elements
    // in t here is fixed. This allows us to just get all ENTRY tags and access them in the right order to identify the
    // data we need.
    const startupDateLabelIndex = 0; // ENTR Y[0]: start-up date label
    const startupDateRadioIndex = 1; // ENTR Y[1]: radio button for startup date
    const dateDayIndex = 2; // ENTR Y[2]: day field
    // ENTR Y[3]: dot as separator for date; just ignore
    const dateMonthIndex = 4; // ENTR Y[4]: month field
    // ENTR Y[5]: dot as separator for date; just ignore
    const dateYearIndex = 6; // ENTR Y[6]: year field
    const otherDateLabelIndex = 7; // ENTR Y[7]: other date label, eg. replacement date or last procedure date
    const otherDateRadioIndex = 8; // ENTR Y[8]: radio button for replacement date
    // ENTR Y[9...15]: labels; just ignore

    // Firs t get the labels for the radio buttons
    const startupDateLabelNode = getFirstNode(doc, entryNodes[startupDateLabelIndex], './/CELLPARA');
    if (!isDefined(startupDateLabelNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getReplacementOrLastProcedureDate: Could not identify label for startup date PDFFORM in functional element '${parentId}'`,
        );
    }
    const startupDateLabel = scrubXmlTags(startupDateLabelNode.innerHTML);
    const otherDateLabelNode = getFirstNode(doc, entryNodes[otherDateLabelIndex], './/CELLPARA');
    if (!isDefined(otherDateLabelNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getReplacementOrLastProcedureDate: Could not identify label for replacement date PDFFORM in functional element '${parentId}'`,
        );
    }
    const replacementDateLabel = scrubXmlTags(otherDateLabelNode.innerHTML);

    // then the radio buttons themselves
    let selected = REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION.NONE;
    const startupDateRadioNode = getFirstNode(doc, entryNodes[startupDateRadioIndex], 'descendant::PDFFORM');
    if (!isDefined(startupDateRadioNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getReplacementOrLastProcedureDate: Could not identify radio button for start-up date PDFFORM in functional element '${parentId}'`,
        );
    }
    // the  xmlId of the radio button is the same for startupDate and replacementDate - they are essentially radio buttons of a checkpoint
    const radioButtonXmlId = startupDateRadioNode.getAttribute('TITLE') ?? '';
    selected =
        startupDateRadioNode.getAttribute('SELECTED') === RADIO_OPTION_SELECTED.YES
            ? REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION.STARTUP_DATE
            : selected;
    const otherDateRadioNode = getFirstNode(doc, entryNodes[otherDateRadioIndex], 'descendant::PDFFORM');
    if (!isDefined(otherDateRadioNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getReplacementOrLastProcedureDate: Could not identify radio button for other date PDFFORM in functional element '${parentId}'`,
        );
    }
    selected =
        otherDateRadioNode.getAttribute('SELECTED') === RADIO_OPTION_SELECTED.YES
            ? REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION.OTHER_DATE
            : selected;

    // now  we just need the input fields for the date itself
    const radixParameter = 10;
    const dateDayNode = getFirstNode(doc, entryNodes[dateDayIndex], 'descendant::PDFFORM');
    if (!isDefined(dateDayNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getReplacementOrLastProcedureDate: Could not identify day PDFFORM in functional element '${parentId}'`,
        );
    }
    const dayXmlId = dateDayNode.getAttribute('TITLE') ?? '';
    const dayValue = dateDayNode.textContent ?? '';
    const day = dayValue.length > 0 ? parseInt(dateDayNode.textContent || '', radixParameter) : NaN;
    const dateMonthNode = getFirstNode(doc, entryNodes[dateMonthIndex], 'descendant::PDFFORM');
    if (!isDefined(dateMonthNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getReplacementOrLastProcedureDate: Could not identify day PDFFORM in functional element '${parentId}'`,
        );
    }
    const monthXmlId = dateMonthNode.getAttribute('TITLE') ?? '';
    const monthValue = dateMonthNode.textContent ?? '';
    // beca use the month is returned by the first 3 Characters(eg Jan), we need to get the moment month number.
    const month = monthValue.length > 0 ? parseInt(moment().month(monthValue).format('MM'), radixParameter) - 1 : NaN;
    const dateYearNode = getFirstNode(doc, entryNodes[dateYearIndex], 'descendant::PDFFORM');
    if (!isDefined(dateYearNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getReplacementOrLastProcedureDate: Could not identify day PDFFORM in functional element '${parentId}'`,
        );
    }
    const yearXmlId = dateYearNode.getAttribute('TITLE') ?? '';
    const yearValue = dateYearNode.textContent ?? '';
    const year = yearValue.length > 0 ? parseInt(yearValue, radixParameter) : NaN;

    const date = moment([year, month, day]).isValid() ? moment([year, month, day]).toISOString() : '';

    const contentLoadedFromXml = selected !== REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION.NONE || date.length > 0;

    // iden tify controlling checkpoint
    const checkpointXmlIdPath = './preceding-sibling::*[1][self::LOP-ENTRY]/CHECKBTN/PDFFORM/@TITLE';
    const controllingCheckpoint =
        doc.evaluate(checkpointXmlIdPath, node, null, XPathResult.STRING_TYPE, null).stringValue ?? '';

    return {
        id: xmlNodeIdCounter++,
        parentId,
        contentLoadedFromXml,
        controllingCheckpoint,
        startupDateLabel,
        radioButtonXmlId,
        replacementDateLabel,
        selected,
        date,
        dayXmlId,
        monthXmlId,
        yearXmlId,
        disabled: false,
        controllingOption: undefined,
        type,
    };
}

function getMaterialSerialNumber(
    doc: XMLDocument,
    node: Node,
    parentId: ContentId,
    type: FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO | FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO_WITH_DESC,
): MaterialSerialNumber {
    // depending on the type of the functional element, there will be a different number of ROW tags for
    // part s of the functional element.
    // For a MATERIAL_SERIAL_NO, there will first be the tags for the material number, then for the serial number.
    // For MATERIAL_SERIAL_NO_WITH_DESC, there will be first the tags for the description, then material number, then
    // serial number.
    const descriptionRowIndex = 0;
    const materialRowIndex = type === FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO_WITH_DESC ? 1 : 0;

    const serialRowIndex = type === FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO_WITH_DESC ? 2 : 1;

    const rowNodes = getChildNodes(doc, node, './/ROW');

    let descriptionLabel = '';
    let descriptionXmlId = '';
    let descriptionValue = '';
    if (type === FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO_WITH_DESC) {
        const descriptionInputFieldNode = getFirstNode(doc, rowNodes[descriptionRowIndex], './/PDFFORM');
        if (!isDefined(descriptionInputFieldNode)) {
            throw new Error(
                `AstoriaXmlUtilities: getMaterialSerialNumber: Could not identify description PDFFORM in functional element '${parentId}'`,
            );
        }
        descriptionValue = descriptionInputFieldNode.textContent ?? '';
        descriptionXmlId = descriptionInputFieldNode.getAttribute('TITLE') ?? '';

        const descriptionLabelNode = getFirstNode(doc, rowNodes[descriptionRowIndex], './/CELLPARA[1]');
        if (!isDefined(descriptionLabelNode)) {
            throw new Error(
                `AstoriaXmlUtilities: getMaterialSerialNumber: Could not identify label for description PDFFORM in functional element '${parentId}'`,
            );
        }
        descriptionLabel = scrubXmlTags(descriptionLabelNode.textContent ?? '');
    }

    const materialInputFieldNode = getFirstNode(doc, rowNodes[materialRowIndex], './/PDFFORM');
    if (!isDefined(materialInputFieldNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getMaterialSerialNumber: Could not identify material PDFFORM in functional element '${parentId}'`,
        );
    }
    const materialValue = materialInputFieldNode.textContent ?? '';
    const materialXmlId = materialInputFieldNode.getAttribute('TITLE') ?? '';

    const materialLabelNode = getFirstNode(doc, rowNodes[materialRowIndex], './/CELLPARA[1]');
    if (!isDefined(materialLabelNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getMaterialSerialNumber: Could not identify label for material PDFFORM in functional element '${parentId}'`,
        );
    }
    const materialLabel = scrubXmlTags(materialLabelNode.textContent ?? '');

    const serialInputFieldNode = getFirstNode(doc, rowNodes[serialRowIndex], './/PDFFORM');
    if (!isDefined(serialInputFieldNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getMaterialSerialNumber: Could not identify serial PDFFORM in functional element '${parentId}'`,
        );
    }
    const serialValue = serialInputFieldNode.textContent ?? '';
    const serialXmlId = serialInputFieldNode.getAttribute('TITLE') ?? '';

    const serialLabelNode = getFirstNode(doc, rowNodes[serialRowIndex], './/CELLPARA[1]');
    if (!isDefined(serialLabelNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getMaterialSerialNumber: Could not identify label for serial PDFFORM in functional element '${parentId}'`,
        );
    }
    const serialLabel = scrubXmlTags(serialLabelNode.textContent ?? '');

    // Try  to find controlling (=directly preceding) checkpoint
    const checkpointXmlIdPath = './preceding-sibling::*[1][self::LOP-ENTRY]/CHECKBTN/PDFFORM/@TITLE';
    const controllingCheckpoint =
        doc.evaluate(checkpointXmlIdPath, node, null, XPathResult.STRING_TYPE, null).stringValue ?? '';

    return {
        id: xmlNodeIdCounter++,
        materialLabel,
        materialXmlId,
        materialValue,
        parentId,
        serialLabel,
        serialXmlId,
        serialValue,
        descriptionLabel,
        descriptionValue,
        descriptionXmlId,
        type,
        controllingCheckpoint,
        contentLoadedFromXml: materialValue.length > 0 || serialValue.length > 0,
        disabled: false,
        controllingOption: undefined,
    };
}

function getValueFunctionalElement(
    doc: XMLDocument,
    node: Node,
    parentId: ContentId,
    type: FUNCTIONAL_ELEMENT_TYPE.VALUE | FUNCTIONAL_ELEMENT_TYPE.VALUE_UNIT,
): ValueFunctionalElement {
    const valueInputFieldNode = getFirstNode(doc, node, 'descendant::PDFFORM');
    if (!isDefined(valueInputFieldNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getValueFunctionalElement: Could not identify value PDFFORM in functional element '${parentId}'`,
        );
    }
    const value = valueInputFieldNode.textContent ?? '';
    const xmlId = valueInputFieldNode.getAttribute('TITLE') ?? '';

    const valueLabelNode = getFirstNode(doc, node, './/CELLPARA');
    if (!isDefined(valueLabelNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getValueFunctionalElement: Could not identify label for value PDFFORM in functional element '${parentId}'`,
        );
    }
    const label = scrubXmlTags(valueLabelNode.textContent ?? '');

    // Try  to find controlling (=directly preceding) checkpoint
    const checkpointXmlIdPath = './preceding-sibling::*[1][self::LOP-ENTRY]/CHECKBTN/PDFFORM/@TITLE';
    const controllingCheckpoint =
        doc.evaluate(checkpointXmlIdPath, node, null, XPathResult.STRING_TYPE, null).stringValue ?? '';

    return {
        id: xmlNodeIdCounter++,
        label,
        xmlId,
        value,
        parentId,
        type,
        controllingCheckpoint,
        contentLoadedFromXml: value.length > 0,
        disabled: false,
        controllingOption: undefined,
        disabledNACheckbox: false,
    };
}

function getOption(doc: XMLDocument, node: Node, parentId: ContentId): Option {
    const entryNodes = getChildNodes(doc, node, './/ENTRY');
    if (!isDefined(entryNodes)) {
        throw new Error(
            `AstoriaXmlUtilities: getOption: Could not identify ENTRY tags in functional element '${parentId}'`,
        );
    }

    // the  XML structure for option is not too nice, but fortunately the order of elements
    // in t here is fixed. This allows us to just get all ENTRY tags and access them in the right order to identify the
    // data we need where we don't have better options.
    // ENTR Y[0]: label
    const yesLabelIndex = 1; // ENTR Y[1]: label for radio button 'yes'
    // ENTR Y[2]: radio button for 'yes'
    const noLabelIndex = 3; // ENTR Y[3]: dot as separator for date; just ignore
    // ENTR Y[4]: radio button for 'no'
    // ENTR Y[5...12]: Signature, date, name: as per specification these can be ignored

    const yesRadioNode = getFirstNode(doc, node, `descendant::PDFFORM[@TYPE='${OPTION_FUNCTIONAL_ELEMENT_TYPE.YES}']`);
    if (!isDefined(yesRadioNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getOption: Could not identify radio button for 'yes' in functional element '${parentId}'`,
        );
    }
    const xmlId = yesRadioNode.getAttribute('TITLE') ?? '';
    let selected: AstoriaOption['selected'] =
        yesRadioNode.getAttribute('SELECTED') === RADIO_OPTION_SELECTED.YES
            ? OPTION_FUNCTIONAL_ELEMENT_TYPE.YES
            : undefined;

    const noRadioNode = getFirstNode(doc, node, `descendant::PDFFORM[@TYPE='${OPTION_FUNCTIONAL_ELEMENT_TYPE.NO}']`);
    if (!isDefined(noRadioNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getOption: Could not identify radio button for 'no' in functional element '${parentId}'`,
        );
    }
    selected =
        noRadioNode.getAttribute('SELECTED') === RADIO_OPTION_SELECTED.YES
            ? OPTION_FUNCTIONAL_ELEMENT_TYPE.NO
            : selected;

    const optionLabelNode = getFirstNode(doc, node, './/CELLPARA');
    if (!isDefined(optionLabelNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getOption: Could not identify label for radio buttons in functional element '${parentId}'`,
        );
    }
    const label = scrubXmlTags(optionLabelNode.textContent ?? '');

    const yesLabelNode = getFirstNode(doc, entryNodes[yesLabelIndex], './/CELLPARA');
    if (!isDefined(yesLabelNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getOption: Could not identify label for radio button 'yes' in functional element '${parentId}'`,
        );
    }
    const yesLabel = scrubXmlTags(yesLabelNode.innerHTML);

    const noLabelNode = getFirstNode(doc, entryNodes[noLabelIndex], './/CELLPARA');
    if (!isDefined(noLabelNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getOption: Could not identify label for radio button 'no' in functional element '${parentId}'`,
        );
    }
    const noLabel = scrubXmlTags(noLabelNode.innerHTML);

    return {
        id: xmlNodeIdCounter++,
        label,
        yesLabel,
        noLabel,
        xmlId,
        selected,
        parentId,
        type: FUNCTIONAL_ELEMENT_TYPE.OPTION,
        // OPTI ON never has a controlling checkpoint, since it functions as a controlling element itself
        controllingCheckpoint: '',
        contentLoadedFromXml: typeof selected !== 'undefined',
        disabled: false,
        controllingOption: undefined,
    };
}

function getWorkstepsPerformed(doc: XMLDocument, node: Node, parentId: ContentId): WorkstepsPerformed {
    return {
        id: xmlNodeIdCounter++,
        parentId,
        contentLoadedFromXml: false,
        controllingCheckpoint: '',
        disabled: false,
        type: FUNCTIONAL_ELEMENT_TYPE.WORKSTEPS_PERFORMED,
        controllingOption: undefined,
    };
}

function getSignature(
    doc: XMLDocument,
    doctype: string,
    node: Node,
    parentId: ContentId,
    type:
        | FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE
        | FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER
        | FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE_SYSTEM_CONDITION
        | FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER_SYSTEM_CONDITION,
    supportsParticipantCopies: boolean,
): Signature {
    // firs t get data out of the FIGURE element
    const figureElement = getFirstNode(doc, node, './/FIGURE');
    const entity = figureElement?.getAttribute('ENTITY');
    const rasterDpi = figureElement?.getAttribute('RASTERDPI');
    const heightInch = figureElement?.getAttribute('HEIGHT');
    const widthInch = figureElement?.getAttribute('WIDTH');
    if (!entity || !rasterDpi || !heightInch || !widthInch) {
        throw new Error(
            `AstoriaXmlUtilities: getSignature: Not all required info was found (entity: ${entity}, rasterDpi: ${rasterDpi}, heightInch: ${heightInch}, widthInch: ${widthInch})`,
        );
    }

    // Base d on the entity for the signature, we need to identify the right entity definition in the doctype to extract
    // the  full path of the corresponding signature image in the publication package. Unfortunately the doctype is not
    // repr esented in the generated DOM structure, so we need to find this using a regex on the saved doctype string.

    // find & replace entity definition line in doctype
    const entityRegex = new RegExp(`<!ENTITY ${entity} SYSTEM "([a-z0-9\\\\/-]*?.eps)" NDATA\\s+eps>`, 'i');

    const entityMatches = doctype.match(entityRegex);
    if (!entityMatches) {
        throw new Error(
            `AstoriaXmlUtilities: getSignature: Could not find entity definition for signature with entity '${entity}'`,
        );
    }

    // get  match of entity path
    const entityPath = entityMatches[1];

    const dpi = parseFloat(rasterDpi);
    if (isNaN(dpi)) {
        throw new Error(`AstoriaXmlUtilities: getSignature: Could not parse DPI (rasterDpi: ${rasterDpi})`);
    }

    const inchOffset = 2; // meas urement is given as eg. "1.292in" and we need to get rid of the trailing "in"
    const height = parseFloat(heightInch.substring(0, heightInch.length - inchOffset)) * dpi;
    const width = parseFloat(widthInch.substring(0, widthInch.length - inchOffset)) * dpi;
    if (isNaN(height) || isNaN(width)) {
        throw new Error(
            `AstoriaXmlUtilities: getSignature: Could not parse height and width (heightInch: ${heightInch}, widthInch: ${widthInch})`,
        );
    }

    // seco nd, identify the description and label for the signature
    const descriptionNodes = getChildNodes(doc, node, './/ROW[1]/ENTRY/CELLPARA');
    if (descriptionNodes.length === 0) {
        throw new Error(
            `AstoriaXmlUtilities: getSignature: Could not find description for signature with id '${parentId}'`,
        );
    }
    const description = scrubXmlTags(
        descriptionNodes.map((descriptionNode) => descriptionNode.textContent ?? '').join(' '),
    );

    const labelNodes = getChildNodes(doc, node, './/ROW[2]/ENTRY/CELLPARA');
    if (labelNodes.length === 0) {
        throw new Error(`AstoriaXmlUtilities: getSignature: Could not find label for signature with id '${parentId}'`);
    }
    const label = scrubXmlTags(labelNodes.map((labelNode) => labelNode.textContent ?? '').join(' '));

    // thir d, find the PDFFORM element for the date
    const dateNode = getFirstNode(doc, node, 'descendant::PDFFORM');
    if (!dateNode) {
        throw new Error(
            `AstoriaXmlUtilities: getSignature: Could not find PDFFORM element for date in signature with id '${parentId}'`,
        );
    }

    // we d o not restore signature dates, as the user is supposed to re-enter the signature every time.
    const date = '';
    const dateXmlId = dateNode.getAttribute('TITLE') ?? '';
    if (!dateXmlId) {
        throw new Error(
            `AstoriaXmlUtilities: getSignature: Could not find xmlId of PDFFORM element for date in signature with id '${parentId}'`,
        );
    }

    // four th, find the PDFFORM element for the signee's name
    const nameNode = getLastNode(doc, node, 'descendant::PDFFORM');
    if (!nameNode) {
        throw new Error(
            `AstoriaXmlUtilities: getSignature: Could not find PDFFORM element for name in signature with id '${parentId}'`,
        );
    }

    const nameXmlId = nameNode.getAttribute('TITLE') ?? '';
    if (!nameXmlId) {
        throw new Error(
            `AstoriaXmlUtilities: getSignature: Could not find xmlId of PDFFORM element for name in signature with id '${parentId}'`,
        );
    }

    // we d o not restore signature names, as the user is supposed to re-enter the signature every time.
    const lastName = '';
    const firstName = '';

    // fift h, identify controlling checkpoint
    const checkpointXmlIdPath = './preceding-sibling::*[1][self::LOP-ENTRY]/CHECKBTN/PDFFORM/@TITLE';
    const controllingCheckpoint =
        doc.evaluate(checkpointXmlIdPath, node, null, XPathResult.STRING_TYPE, null).stringValue ?? '';

    // Cont rolling checkpoint is mandatory for signatures in the form body, but not for the prologue
    if (
        controllingCheckpoint.length === 0 &&
        (type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE || type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER)
    ) {
        throw new Error(
            `AstoriaXmlUtilities: getSignature: Could not find controlling checkpoint for signature with id '${parentId}'`,
        );
    }

    return {
        id: xmlNodeIdCounter++,
        signature: '',
        entity,
        entityPath,
        label,
        description,
        height,
        width,
        date,
        dateXmlId,
        firstName,
        lastName,
        nameXmlId,
        controllingCheckpoint,
        signatureDataPoints: [], // not  stored in XML, but needed for signature component
        email: '', // not  stored in XML, but needed for signature component
        emailRequired: type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER && supportsParticipantCopies,
        parentId,
        type,
        contentLoadedFromXml: false,
        disabled: false,
        controllingOption: undefined,
    };
}

function getSystemConditionForMaintenance(
    doc: XMLDocument,
    doctype: string,
    supportsParticipantCopies: boolean,
): SystemCondition {
    const heading = scrubXmlTags(getFirstNode(doc, doc, '//PROTOCOL/SECTION[1]/SECTION[1]/HEADING')?.textContent ?? '');

    /* esli nt-disable no-magic-numbers */
    const deficienciesTableIndex = 1;
    const employeeSignatureTableIndex = 2;
    const customerSignatureTableIndex = 3;

    const noDeficienciesIndex = 0;
    const slightDeficienciesIndex = 1;
    const seriousDeficienciesIndex = 2;

    const labelIndex = 1;
    /* esli nt-enable no-magic-numbers */

    const deficienciesTableNode = getFirstNode(
        doc,
        doc,
        `//PROTOCOL/SECTION[1]/SECTION[1]/TABLE[${deficienciesTableIndex}]`,
    );
    if (!isDefined(deficienciesTableNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getSystemConditionForMaintenance: Could not find table node for deficiencies`,
        );
    }
    const deficienciesRowNodes = getChildNodes(doc, deficienciesTableNode, './/ROW');

    const noDeficienciesLabel = scrubXmlTags(
        getFirstNode(doc, deficienciesRowNodes[noDeficienciesIndex], `.//CELLPARA[position()=${labelIndex}]`)
            ?.textContent ?? '',
    );
    const slightDeficienciesLabel = scrubXmlTags(
        getFirstNode(doc, deficienciesRowNodes[slightDeficienciesIndex], `.//CELLPARA[position()=${labelIndex}]`)
            ?.textContent ?? '',
    );
    const seriousDeficienciesLabel = scrubXmlTags(
        getFirstNode(doc, deficienciesRowNodes[seriousDeficienciesIndex], `.//CELLPARA[position()=${labelIndex}]`)
            ?.textContent ?? '',
    );
    const deficienciesXmlId =
        getFirstNode(doc, deficienciesRowNodes[noDeficienciesIndex], './/PDFFORM')?.getAttribute('TITLE') ?? '';

    const employeeSignatureNode = getFirstNode(
        doc,
        doc,
        `//PROTOCOL/SECTION[position()=1]/SECTION[position()=1]/TABLE[${employeeSignatureTableIndex}]`,
    );
    if (!isDefined(employeeSignatureNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getSystemConditionForMaintenance: Could not find node for employee signature`,
        );
    }
    const employeeSignature = getSignature(
        doc,
        doctype,
        employeeSignatureNode,
        -1,
        FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE_SYSTEM_CONDITION,
        supportsParticipantCopies,
    );

    const customerSignatureNode = getFirstNode(
        doc,
        doc,
        `//PROTOCOL/SECTION[1]/SECTION[1]/TABLE[${customerSignatureTableIndex}]`,
    );
    if (!isDefined(customerSignatureNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getSystemConditionForMaintenance: Could not find node for customer signature`,
        );
    }
    const customerSignature = getSignature(
        doc,
        doctype,
        customerSignatureNode,
        -1,
        FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER_SYSTEM_CONDITION,
        supportsParticipantCopies,
    );

    const imagingRemark = scrubXmlTags(
        getFirstNode(doc, doc, '//PROTOCOL/SECTION[1]/SECTION[1]/PARA[1]')?.textContent ?? '',
    );
    const remarksLabel = scrubXmlTags(
        getFirstNode(doc, doc, '//PROTOCOL/SECTION[1]/SECTION[1]/PARA[2]')?.textContent ?? '',
    );
    const remarksInputNode = getFirstNode(doc, doc, '//PROTOCOL/SECTION[1]/SECTION[1]/TABLE[4]//PDFFORM[1]');
    const remarksXmlId = remarksInputNode?.getAttribute('TITLE') ?? '';
    const remarksValue = remarksInputNode?.textContent ?? '';

    return {
        heading,
        deficienciesXmlId,
        noDeficienciesLabel,
        slightDeficienciesLabel,
        seriousDeficienciesLabel,
        deficienciesSelection: DEFICIENCY_SELECTION.NO_SELECTION,
        customerSignature,
        employeeSignature,
        imagingRemark,
        remarksLabel,
        remarksXmlId,
        remarksValue,
    };
}

function getSystemConditionForSafety(
    doc: XMLDocument,
    doctype: string,
    supportsParticipantCopies: boolean,
): SystemCondition {
    const heading = scrubXmlTags(getFirstNode(doc, doc, '//PROTOCOL/SECTION[1]/SECTION[1]/HEADING')?.textContent ?? '');

    /* eslint no-magic-numbers: 0, @typescript-eslint/no-unused-vars: 0 */
    const measurementTableIndex = 1;
    const initialMeasurementRadioIndex = 0;
    const initialMeasurementIndex = 1;
    const repeatMeasurementIndex = 3;

    const deficienciesTableIndex = 2;
    // not able index for employee signature since it is hidden in the deficiencies table because nothing makes sense in the
    // prologue world
    const customerSignatureTableIndex = 3;

    const employeeSignatureDescriptionIndex = 0;
    const noDeficienciesIndex = 1;
    const slightDeficienciesIndex = 2;
    const seriousDeficienciesIndex = 3;
    const employeeSignatureIndex = 4;

    const labelIndex = 2;

    const { initialMeasurementLabel, repeatMeasurementLabel, measurementXmlId } = getSystemConditionMeasurements(doc);

    const deficienciesTableNode = getFirstNode(
        doc,
        doc,
        `//PROTOCOL/SECTION[1]/SECTION[1]/TABLE[${deficienciesTableIndex}]`,
    );
    if (!isDefined(deficienciesTableNode)) {
        throw new Error(`AstoriaXmlUtilities: getSystemConditionForSafety: Could not find table node for deficiencies`);
    }
    const deficienciesRowNodes = getChildNodes(doc, deficienciesTableNode, './/ROW');

    const noDeficienciesLabel = scrubXmlTags(
        getFirstNode(doc, deficienciesRowNodes[noDeficienciesIndex], `.//ENTRY[${labelIndex}]/CELLPARA`)?.textContent ??
            '',
    );
    const slightDeficienciesLabel = scrubXmlTags(
        getFirstNode(doc, deficienciesRowNodes[slightDeficienciesIndex], `.//ENTRY[${labelIndex}]/CELLPARA`)
            ?.textContent ?? '',
    );
    const seriousDeficienciesLabel = scrubXmlTags(
        getFirstNode(doc, deficienciesRowNodes[seriousDeficienciesIndex], `.//ENTRY[${labelIndex}]/CELLPARA`)
            ?.textContent ?? '',
    );
    const deficienciesXmlId =
        getFirstNode(doc, deficienciesRowNodes[noDeficienciesIndex], './/PDFFORM')?.getAttribute('TITLE') ?? '';

    // get employee signature from the middle of the table:
    // get data out of the FIGURE element
    const figureElement = getFirstNode(doc, deficienciesRowNodes[employeeSignatureIndex], './/FIGURE');
    const entity = figureElement?.getAttribute('ENTITY');
    const rasterDpi = figureElement?.getAttribute('RASTERDPI');
    const heightInch = figureElement?.getAttribute('HEIGHT');
    const widthInch = figureElement?.getAttribute('WIDTH');
    if (!entity || !rasterDpi || !heightInch || !widthInch) {
        throw new Error(
            `AstoriaXmlUtilities: getSystemConditionForSafety: Not all required info for employee signature was found (entity: ${entity}, rasterDpi: ${rasterDpi}, heightInch: ${heightInch}, widthInch: ${widthInch})`,
        );
    }

    // Based on the entity for the signature, we need to identify the right entity definition in the doctype to extract
    // the full path of the corresponding signature image in the publication package. Unfortunately the doctype is not
    // represented in the generated DOM structure, so we need to find this using a regex on the saved doctype string.

    // find & replace entity definition line in doctype
    const entityRegex = new RegExp(`<!ENTITY ${entity} SYSTEM "([a-z0-9\\\\/-]*?.eps)" NDATA\\s+eps>`, 'i');

    const entityMatches = doctype.match(entityRegex);
    if (!entityMatches) {
        throw new Error(
            `AstoriaXmlUtilities: getSystemConditionForSafety: Could not find entity definition for employee signature with entity '${entity}'`,
        );
    }

    // get  match of entity path
    const entityPath = entityMatches[1];

    const dpi = parseFloat(rasterDpi);
    if (isNaN(dpi)) {
        throw new Error(
            `AstoriaXmlUtilities: getSystemConditionForSafety: Could not parse DPI of employee signature (rasterDpi: ${rasterDpi})`,
        );
    }

    const inchOffset = 2; // meas urement is given as eg. "1.292in" and we need to get rid of the trailing "in"
    const height = parseFloat(heightInch.substring(0, heightInch.length - inchOffset)) * dpi;
    const width = parseFloat(widthInch.substring(0, widthInch.length - inchOffset)) * dpi;
    if (isNaN(height) || isNaN(width)) {
        throw new Error(
            `AstoriaXmlUtilities: getSystemConditionForSafety: Could not parse height and width of employee signature (heightInch: ${heightInch}, widthInch: ${widthInch})`,
        );
    }

    // find label for signature
    const labelNodes = getChildNodes(doc, deficienciesRowNodes[employeeSignatureIndex], './/ENTRY/CELLPARA');
    if (labelNodes.length === 0) {
        throw new Error(
            `AstoriaXmlUtilities: getSystemConditionForSafety: Could not find label for employee signature`,
        );
    }
    const label = scrubXmlTags(labelNodes.map((labelNode) => labelNode.textContent ?? '').join(' '));

    // identify the description for the signature
    const descriptionNodes = getChildNodes(
        doc,
        deficienciesRowNodes[employeeSignatureDescriptionIndex],
        './/ENTRY/CELLPARA',
    );
    if (descriptionNodes.length === 0) {
        throw new Error(
            `AstoriaXmlUtilities: getSystemConditionForSafety: Could not find description for employee signature`,
        );
    }
    const description = scrubXmlTags(
        descriptionNodes.map((descriptionNode) => descriptionNode.textContent ?? '').join(' '),
    );

    // find the PDFFORM element for the date
    const dateNode = getFirstNode(
        doc,
        deficienciesRowNodes[employeeSignatureIndex + 1],
        './descendant::*[self::PDFFORM or self::PDARESULT][1]',
    );
    if (!dateNode) {
        throw new Error(
            `AstoriaXmlUtilities: getSystemConditionForSafety: Could not find PDFFORM element for date in employee signature`,
        );
    }

    // we do not restore signature dates, as the user is supposed to re-enter the signature every time.
    const date = '';
    const dateElement = dateNode;
    const dateXmlId = dateElement.getAttribute('TITLE') ?? '';
    if (!dateXmlId) {
        throw new Error(
            `AstoriaXmlUtilities: getSystemConditionForSafety: Could not find xmlId of PDFFORM element for date in employee signature`,
        );
    }

    // find the PDFFORM element for the signee's name
    const nameNode = getFirstNode(
        doc,
        deficienciesRowNodes[employeeSignatureIndex + 1],
        './descendant::*[self::PDFFORM or self::PDARESULT][2]',
    );
    if (!nameNode) {
        throw new Error(
            `AstoriaXmlUtilities: getSystemConditionForSafety: Could not find PDFFORM element for name in employee signature`,
        );
    }

    const nameXmlId = nameNode.getAttribute('TITLE') ?? '';
    if (!nameXmlId) {
        throw new Error(
            `AstoriaXmlUtilities: getSystemConditionForSafety: Could not find xmlId of PDFFORM element for name in employee signature`,
        );
    }

    // we do not restore signature names, as the user is supposed to re-enter the signature every time.
    const lastName = '';
    const firstName = '';
    const employeeSignature: Signature = {
        signature: '',
        entity,
        entityPath,
        description,
        label,
        height,
        width,
        date,
        dateXmlId,
        firstName,
        lastName,
        nameXmlId,
        controllingCheckpoint: '',
        id: xmlNodeIdCounter++,
        signatureDataPoints: [], // not  stored in XML, but needed for signature component
        email: '', // not  stored in XML, but needed for signature component
        emailRequired: false,
        parentId: -1,
        type: FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE_SYSTEM_CONDITION,
        contentLoadedFromXml: false,
        disabled: false,
        controllingOption: undefined,
    };

    const customerSignatureNode = getFirstNode(
        doc,
        doc,
        `//PROTOCOL/SECTION[1]/SECTION[1]/TABLE[${customerSignatureTableIndex}]`,
    );
    if (!isDefined(customerSignatureNode)) {
        throw new Error(
            `AstoriaXmlUtilities: getSystemConditionForMaintenance: Could not find node for customer signature`,
        );
    }
    const customerSignature = getSignature(
        doc,
        doctype,
        customerSignatureNode,
        -1,
        FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER_SYSTEM_CONDITION,
        supportsParticipantCopies,
    );

    const remarksLabel = scrubXmlTags(
        getFirstNode(doc, doc, '//PROTOCOL/SECTION[1]/SECTION[1]/PARA[1]')?.textContent ?? '',
    );
    const remarksInputNode = getFirstNode(doc, doc, '//PROTOCOL/SECTION[1]/SECTION[1]/TABLE[4]//PDFFORM[1]');
    const remarksXmlId = remarksInputNode?.getAttribute('TITLE') ?? '';
    const remarksValue = remarksInputNode?.textContent ?? '';

    return {
        heading,
        deficienciesXmlId,
        noDeficienciesLabel,
        slightDeficienciesLabel,
        seriousDeficienciesLabel,
        deficienciesSelection: DEFICIENCY_SELECTION.NO_SELECTION,
        customerSignature,
        employeeSignature,
        imagingRemark: '', // no i maging remark in safety checklist system condition
        remarksLabel,
        remarksXmlId,
        remarksValue,
        initialMeasurementLabel,
        repeatMeasurementLabel,
        measurementXmlId,
        measurementSelection: MEASUREMENT_SELECTION.NO_SELECTION,
    };
}

function getSystemCondition(
    doc: XMLDocument,
    doctype: string,
    type: FormsConstants.FORMTYPE.SAFETY | FormsConstants.FORMTYPE.MAINTENANCE,
    supportsParticipantCopies: boolean,
): { systemCondition: SystemCondition; doctype: string } {
    let systemCondition =
        type === FormsConstants.FORMTYPE.MAINTENANCE
            ? getSystemConditionForMaintenance(doc, doctype, supportsParticipantCopies)
            : getSystemConditionForSafety(doc, doctype, supportsParticipantCopies);

    // ensu re that the two signatures have unique entities
    let newDoctype = doctype;
    if (systemCondition.employeeSignature.entity === systemCondition.customerSignature.entity) {
        const oldEntity = systemCondition.employeeSignature.entity;

        const newCseEntity = oldEntity + FormsConstants.SYSTEM_CONDITION_EMPLOYEE_SIGNATURE_ENTITY_SUFFIX;
        const newCustomerEntity = oldEntity + FormsConstants.SYSTEM_CONDITION_CUSTOMER_SIGNATURE_ENTITY_SUFFIX;

        // upda te the doctype with the new entities
        newDoctype = addAdditionalEntityToDoctype(
            doctype,
            systemCondition.employeeSignature.entity,
            newCseEntity,
            newCustomerEntity,
        );

        // upda te the system condition state
        systemCondition = {
            ...systemCondition,
            employeeSignature: {
                ...systemCondition.employeeSignature,
                entity: newCseEntity,
                entityPath: systemCondition.employeeSignature.entityPath.replace(
                    systemCondition.employeeSignature.entity,
                    newCseEntity,
                ),
            },
            customerSignature: {
                ...systemCondition.customerSignature,
                entity: newCustomerEntity,
                entityPath: systemCondition.customerSignature.entityPath.replace(
                    systemCondition.customerSignature.entity,
                    newCustomerEntity,
                ),
            },
        };

        // Upda te XML with the new signatures as well
        updateSystemConditionSignatureEntity(doc, systemCondition.customerSignature);
        updateSystemConditionSignatureEntity(doc, systemCondition.employeeSignature);
    }

    return { systemCondition, doctype: newDoctype };
}
function getFunctionalElement(
    doc: XMLDocument,
    doctype: string,
    node: Node,
    parentId: ContentId,
    supportsParticipantCopies: boolean,
): FunctionalElement | undefined {
    const functionalElementType = doc.evaluate(
        'string(LOP-TABLE/TABLE/@LOP-TABLE-ID)',
        node,
        null,
        XPathResult.STRING_TYPE,
        null,
    ).stringValue;
    switch (functionalElementType) {
        case FUNCTIONAL_ELEMENT_TYPE.DISCREPANCY_LOG:
            return getDiscrepancyLog(doc, node, parentId);
        case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER:
        case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE:
            return getSignature(doc, doctype, node, parentId, functionalElementType, supportsParticipantCopies);
        case FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO:
        case FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO_WITH_DESC:
            return getMaterialSerialNumber(doc, node, parentId, functionalElementType);
        case FUNCTIONAL_ELEMENT_TYPE.DATE_PERFORMED:
            return getDatePerformed(doc, node, parentId);
        case FUNCTIONAL_ELEMENT_TYPE.REPLACEMENT_DATE:
        case FUNCTIONAL_ELEMENT_TYPE.LAST_PROCEDURE_DATE:
            return getReplacementOrLastProcedureDate(doc, node, parentId, functionalElementType);
        case FUNCTIONAL_ELEMENT_TYPE.VALUE:
        case FUNCTIONAL_ELEMENT_TYPE.VALUE_UNIT:
            return getValueFunctionalElement(doc, node, parentId, functionalElementType);
        case FUNCTIONAL_ELEMENT_TYPE.OPTION:
            return getOption(doc, node, parentId);
        case FUNCTIONAL_ELEMENT_TYPE.WORKSTEPS_PERFORMED:
            return getWorkstepsPerformed(doc, node, parentId);
        default:
            if (functionalElementType.length > 0) {
                console.warn(
                    `AstoriaXmlUtilities: getFunctionalElement: Encountered unknown functional element type '${functionalElementType}'`,
                );
            }
            return undefined;
    }
}
function getContentBlock(
    doc: XMLDocument,
    doctype: string,
    node: Node,
    parentId: number,
    contentBlocks: Mutable<ContentBlocks>,
    functionalElements: Mutable<FunctionalElements>,
    optionDependencies: Mutable<OptionDependencies>,
    headings: Mutable<Headings>,
    checkpoints: Mutable<Checkpoints>,
    parentOption: Option | undefined,
    supportsParticipantCopies: boolean,
): { contentBlock: ContentBlock; workStepsPerformed: boolean } {
    // Chec k for LOP-ENTRY children
    const childNodes = getChildNodes(doc, node, 'LOP-ENTRY|LOP-CONTENT');
    let heading: Heading | undefined;
    let functionalElement: Mutable<FunctionalElement> | undefined;
    let checkpoint: Mutable<Checkpoint> | undefined;
    let contentBlock: ContentBlock | undefined;
    const children: ContentChild[] = [];
    const id = xmlNodeIdCounter++;
    let currentOption = parentOption;
    let workStepsPerformed = false;

    for (const child of childNodes) {
        if (child.nodeName === 'LOP-ENTRY') {
            // this node contains data, now extract it
            heading = getHeading(doc, child, id);
            if (typeof heading !== 'undefined') {
                headings[heading.id] = heading;
                children.push({ id: heading.id, type: CONTENT_TYPE.HEADING });
            }

            checkpoint = getCheckpoint(doc, child, id);
            if (typeof checkpoint !== 'undefined') {
                checkpoints[checkpoint.id] = checkpoint;
                children.push({ id: checkpoint.id, type: CONTENT_TYPE.CHECKPOINT });
            }

            functionalElement = getFunctionalElement(doc, doctype, child, id, supportsParticipantCopies);
            if (typeof functionalElement !== 'undefined') {
                // only add the functional element if it is not WORKSTEPS_PERFORMED. That functional element will be
                // igno red in the UI; its only purpose is to close an optional section that was opened by OPTION.
                if (functionalElement.type !== FUNCTIONAL_ELEMENT_TYPE.WORKSTEPS_PERFORMED) {
                    functionalElements[functionalElement.id] = functionalElement;
                    children.push({ id: functionalElement.id, type: CONTENT_TYPE.FUNCTIONAL });
                } else {
                    currentOption = undefined;
                    workStepsPerformed = true;
                }

                if (functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.OPTION) {
                    if (typeof currentOption !== 'undefined') {
                        throw new Error(
                            `AstoriaXmlUtilities: getContentBlock: Encountered nested OPTION functional element, which is not supported!`,
                        );
                    }
                    currentOption = functionalElement as Option;
                }
            }

            if (typeof currentOption !== 'undefined' && currentOption !== null) {
                const dependency: OptionDependency = optionDependencies[currentOption.id] ?? {
                    functionalElements: [],
                    checkpoints: [],
                    option: currentOption.id,
                };

                // add  a checkpoint if the entry contained one
                if (typeof checkpoint !== 'undefined') {
                    checkpoint.controllingOption = currentOption.id;
                    dependency.checkpoints.push(checkpoint.id);
                } else if (typeof functionalElement !== 'undefined' && !isAstoriaOption(functionalElement)) {
                    // add  a functional element if the entry contained one. But: Functional element OPTION cannot depend on
                    // itse lf, so we only add functional elements of other types
                    functionalElement.controllingOption = currentOption.id;
                    dependency.functionalElements.push(functionalElement.id);
                }

                optionDependencies[currentOption.id] = dependency;

                // open ing a new OPTION should not be influenced by any pending WORKSTEPS_PERFORMED, so we reset it here
                workStepsPerformed = false;
            }
        } else {
            // this is a LOP-CONTENT, recursively call this function again to get the data
            const content = getContentBlock(
                doc,
                doctype,
                child,
                id,
                contentBlocks,
                functionalElements,
                optionDependencies,
                headings,
                checkpoints,
                currentOption,
                supportsParticipantCopies,
            );

            contentBlock = content.contentBlock;
            contentBlocks[contentBlock.id] = contentBlock;
            children.push({ id: contentBlock.id, type: CONTENT_TYPE.CONTENT });

            // if a child had contained a "WORKSTEPS_PERFORMED", we need to check if we can close the local OPTION
            if (content.workStepsPerformed && typeof currentOption !== 'undefined') {
                currentOption = undefined;
                workStepsPerformed = false; // if w e had an OPTION, then the WORKSTEPS_PERFORMED is now consumed
            }
        }
    }

    return {
        contentBlock: {
            ...initialContentBlockState,
            id,
            children,
            parentId,
        },
        workStepsPerformed,
    };
}

function getCheckpointsAndFunctionalElements(
    doc: XMLDocument,
    initialTypeFilters: FormTypeFilters,
    doctype: string,
    supportsParticipantCopies: boolean,
    formType: FORMTYPE,
): DynamicContentResults {
    const path = '//LISTOFCHECKPNT';
    const contentRoot = getFirstNode(doc, doc, path);
    if (!isDefined(contentRoot)) {
        throw new Error(
            `AstoriaXmlUtilities: getCheckpointsAndFunctionalElements: Could not find node for path '${path}'!`,
        );
    }
    const nodes = getChildNodes(doc, contentRoot, 'LOP-CONTENT');

    // Init ialise the objects and arrays for the state
    const contentBlocks: Mutable<ContentBlocks> = {};
    const headings: Mutable<Headings> = {};
    const functionalElements: Mutable<FunctionalElements> = {};
    const optionDependencies: OptionDependencies = {};
    const checkpoints: Mutable<Checkpoints> = {};

    nodes
        .map((node) =>
            getContentBlock(
                doc,
                doctype,
                node,
                -1,
                contentBlocks,
                functionalElements,
                optionDependencies,
                headings,
                checkpoints,
                undefined,
                supportsParticipantCopies,
            ),
        )
        .map(({ contentBlock, workStepsPerformed }) => contentBlock)
        .forEach((cb) => (contentBlocks[cb.id] = cb));

    const options: Mutable<RadioOptions> = getOptions(doc);

    const checkpointDependencies: Mutable<CheckpointDependencies> = {};
    for (const f in functionalElements) {
        if (functionalElements.hasOwnProperty(f) && functionalElements[f].controllingCheckpoint.length > 0) {
            // setu p the data structure for easy lookup of the dependent functional elements
            const functionalElement = functionalElements[f];
            const controllingCheckpoint = Object.values(checkpoints).find(
                (c) => c.xmlId === functionalElement.controllingCheckpoint,
            );
            if (typeof controllingCheckpoint === 'undefined') {
                throw new Error(
                    `AstoriaXmlUtilities: getContent: Could not find controlling checkpoint with xmlId '${functionalElement.controllingCheckpoint}' for functional element '${functionalElement.id}'`,
                );
            }
            checkpointDependencies[functionalElement.controllingCheckpoint] = {
                functionalElement: functionalElement.id,
                checkpoint: controllingCheckpoint.id,
            };

            // for  each checkpoint dependent functional element, check if the checkpoint is set to 'N.A'.
            // If y es, disable the functional element.
            // We a lso store the currently selected answer for easier lookup later
            const controllingCheckpointOptions = options[functionalElement.controllingCheckpoint];
            if (typeof controllingCheckpointOptions === 'undefined') {
                throw new Error(
                    `AstoriaXmlUtilities: getContent: No options found for controlling checkpoint '${functionalElement.controllingCheckpoint}'`,
                );
            }
            const selectedOption = controllingCheckpointOptions.find((option) => option.selected);

            functionalElements[functionalElement.id] = {
                ...functionalElement,
                disabled:
                    typeof selectedOption !== 'undefined' ? selectedOption.optionType === RADIO_OPTION_TYPE.NA : false,
            };

            // for  signatures we need to ensure that the options of the controlling checkpoints are not marked as
            // load ed from XML, otherwise they would be hidden and the signature mechanism would be broken in
            // part icipant copies as the controlling checkpoint would be hidden.
            const checkpointControlsSignature =
                functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE ||
                functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER;

            options[functionalElement.controllingCheckpoint] = controllingCheckpointOptions.map((option) => ({
                ...option,
                contentLoadedFromXml: option.contentLoadedFromXml && !checkpointControlsSignature,
                // sign ature elements never have their state restored, therefore we need to reset
                // the  checkpoint values controlling the signature functional elements
                selected: checkpointControlsSignature ? false : option.selected,
                selectedInMaster: checkpointControlsSignature ? false : option.selectedInMaster,
            }));
        }
    }

    // For  each filter, check if there are checkpoints for it in the loaded form
    const updatedFilters: { [key in FormsConstants.ELEMENT_TYPE]: TypeFilter } = { ...initialFormTypeFilters };
    Object.values(initialTypeFilters).forEach((f) => {
        const matchingCheckpoint = Object.values(checkpoints).find((c) => c.elementType === f.elementType);
        updatedFilters[f.elementType] = {
            ...initialTypeFilters[f.elementType],
            active: typeof matchingCheckpoint !== 'undefined',
        };
    });
    const typeFilters: FormTypeFilters = updatedFilters;
    const enableManualIndividualization = false;

    // get  the complete answer status for the form
    const answers = AnswerUtilities.getCompleteAnswers(
        FormsConstants.MASTER_PARTICIPANT_ID,
        contentBlocks,
        checkpoints,
        options,
        functionalElements,
        checkpointDependencies,
        typeFilters,
        initialFormFilters,
        enableManualIndividualization,
        formType,
    );

    return {
        headings,
        functionalElements,
        optionDependencies,
        contentBlocks,
        checkpoints,
        checkpointDependencies,
        options,
        answers,
        typeFilters,
    };
}

export class AstoriaXmlUtilities {
    // can  be used to reset AstoriaXmlUtilities for testing
    public static reset(): void {
        xmlNodeIdCounter = 0;
    }

    public static setPdfFormByXmlId(doc: XMLDocument, xmlId: string, newValue: string): void {
        const path = `//PDFFORM[@TITLE='${xmlId}']`;

        const modifiedDateString = moment().toISOString();

        // if t he xmlId is known that might occur multiple times, then we need to update all of the occurrences
        if (xmlIdsWithMultipleOccurrences.includes(xmlId)) {
            const nodes = getChildNodes(doc, doc, path);

            nodes.forEach((node) => {
                node.textContent = newValue;
                // add  or update MODIFIED attribute with timestamp
                node.setAttribute('MODIFIED', modifiedDateString);
            });
        } else {
            const node = getFirstNode(doc, doc, path);
            if (!isDefined(node)) {
                throw new Error(`AstoriaXmlUtilities: setPdfFormByXmlId: Could not find node for path '${path}'!`);
            }
            node.textContent = newValue;

            // add  or update MODIFIED attribute with timestamp
            node.setAttribute('MODIFIED', modifiedDateString);
        }
    }

    public static setCheckbtnByXmlId(doc: XMLDocument, xmlId: string, selected: RADIO_OPTION_TYPE | undefined): void {
        const path = `//PDFFORM[@TITLE='${xmlId}']`;
        const nodes = getChildNodes(doc, doc, path);
        nodes.forEach((node) => {
            // type attribute is one out of 'RADIOBTN.VALUE1', 'RADIOBTN.VALUE2' or 'RADIOBTN.VALUE3'
            // NOT  the type attribute for filtering!
            const optionType = node.getAttribute('TYPE') ?? '';

            if (typeof selected === 'undefined') {
                node.removeAttribute('SELECTED');
            } else {
                node.setAttribute(
                    'SELECTED',
                    optionType === selected ? RADIO_OPTION_SELECTED.YES : RADIO_OPTION_SELECTED.NO,
                );
            }

            // add  or update MODIFIED attribute with timestamp
            node.setAttribute('MODIFIED', moment().toISOString());
        });
    }

    public static getContent(
        doc: XMLDocument,
        doctype: string,
        type: FormsConstants.FORMTYPE,
        typeFilters: FormTypeFilters,
        supportsParticipantCopies: boolean,
    ): ContentResults {
        // chec kpoints and functional elements
        const content = getCheckpointsAndFunctionalElements(doc, typeFilters, doctype, supportsParticipantCopies, type);

        // Eval uation of system condition and hints for filling the form are only available in 832 and 834 forms
        const { systemCondition, doctype: newDoctype } =
            type === FormsConstants.FORMTYPE.MAINTENANCE || type === FormsConstants.FORMTYPE.SAFETY
                ? getSystemCondition(doc, doctype, type, supportsParticipantCopies)
                : { systemCondition: undefined, doctype };

        const hintsForFilling =
            type === FormsConstants.FORMTYPE.MAINTENANCE || type === FormsConstants.FORMTYPE.SAFETY
                ? getHintsForFilling(doc)
                : undefined;

        // extr act values and labels from XML. Will be updated in XmlParserService with notification data.
        const customerIdentification = AstoriaXmlUtilities.getCustomerIdentification(doc);

        // info Class
        const infoClass = getInfoClass(doc);

        // orgD ata
        const orgData = getOrgData(doc);

        // copy right
        const copyright = getCopyright(doc);

        // inst ructions
        const instructions = getInstructions(doc);

        return {
            ...content,
            systemCondition,
            doctype: newDoctype,
            hintsForFilling,
            customerIdentification,
            infoClass,
            orgData,
            copyright,
            instructions,
        };
    }

    /**
     * Removes the attribute MODIFIED from all PDFFORM elements
     *
     * @param doc the XML document to be modified
     */
    public static removeModifiedAttribute(doc: XMLDocument): void {
        const pdfFormNodes = getChildNodes(doc, doc, '//PDFFORM[@MODIFIED]');

        pdfFormNodes.forEach((node) => node.removeAttribute('MODIFIED'));
    }

    public static updateStatus(doc: XMLDocument, statusString: string): void {
        const statusNode = getFirstNode(doc, doc, '//MED-DOC');

        if (!statusNode) {
            throw new Error(`AstoriaXmlUtilities: updateStatus: Could not find MED-DOC node to update status`);
        }

        statusNode.setAttribute('STATUS', statusString);
    }

    /**
     * Remove all checkpoints and functional elements outside of a given date range
     * This functionality was removed from the scope of MARS2, but will probably be needed in the future. Hence keeping
     * the code in here for future use.
     *
     * @param doc the XML document to be modified
     * @param fromDate the start date of the range
     * @param tillDate the end date of the range
     */
    public static removeByDate(doc: XMLDocument, fromDate: moment.Moment, tillDate: moment.Moment): void {
        // Loop over all functional elements (=LOP tables) and check their MODIFIED date
        const functionalElements = getChildNodes(doc, doc, '//LOP-TABLE/TABLE[@LOP-TABLE-ID]');

        // Get  PDFFORM elements inside functional element and check modified date
        functionalElements.forEach((f) => {
            const pdfForms = getChildNodes(doc, f, './/PDFFORM[@MODIFIED]');
            const removeFunctionalElement = hasPdfFormOutsideDateRange(pdfForms, fromDate, tillDate);

            if (
                removeFunctionalElement &&
                f.parentElement !== null && // f.pa rent === LOP-TABLE
                f.parentElement.parentElement !== null && // f.pa rent.parent = LOP-ENTRY
                f.parentElement.parentElement.parentElement !== null // f.pa rent.parent.parent = LOP-CONTENT
            ) {
                f.parentElement.parentElement.parentElement.removeChild(f.parentElement.parentElement);
            }
        });

        // Loop over all checkpoints and check their MODIFIED DATE
        const checkpoints = getChildNodes(doc, doc, '//CHECKPNT');
        checkpoints.forEach((c) => {
            const checkbtn = getFirstNode(doc, c, './following-sibling::*[1][self::CHECKBTN]');
            if (!isDefined(checkbtn)) {
                throw new Error(
                    `AstoriaXmlUtilities: removeByDate: Could not find CHECKBTN for CHECKPNT element with content '${c.textContent}'`,
                );
            }

            const pdfForms = getChildNodes(doc, checkbtn, './/PDFFORM[@MODIFIED]');
            const removeCheckpoint = hasPdfFormOutsideDateRange(pdfForms, fromDate, tillDate);

            if (
                removeCheckpoint &&
                c.parentElement !== null && // c.pa rent === LOP-ENTRY
                c.parentElement.parentElement !== null // c.pa rent.parent === LOP-CONTENT
            ) {
                c.parentElement.parentElement.removeChild(c.parentElement);
            }
        });
    }

    public static removePreviousInterimContent(
        doc: XMLDocument,
        checkpoints: Checkpoints,
        options: RadioOptions,
        functionalElements: FunctionalElements,
    ): void {
        const optionsFromPreviousInterim = Object.values(options)
            .filter(isDefined)
            .filter((optionsWithSameXmlId) => optionsWithSameXmlId.some((option) => option.contentLoadedFromXml))
            .map((optionsWithSameXmlId) => optionsWithSameXmlId[0].xmlId);

        const checkpointsFromPreviousInterim = Object.values(checkpoints).filter((c) =>
            optionsFromPreviousInterim.includes(c.xmlId),
        );
        const functionalElementsFromPreviousInterim = Object.values(functionalElements).filter(
            (f) => f.contentLoadedFromXml,
        );

        // Iter ate over checkpoints whose content was taken from previous interim XML and remove their XML nodes
        checkpointsFromPreviousInterim.forEach((c) => {
            const checkpointPath = `//PDFFORM[@TITLE='${c.xmlId}']//ancestor::LOP-ENTRY`;
            const checkpointNode = getFirstNode(doc, doc, checkpointPath);

            if (checkpointNode !== null && checkpointNode.parentElement !== null) {
                checkpointNode.parentElement.removeChild(checkpointNode);
            }
        });

        // Iter ate over functional elements whose content was taken from previous interim XML and remove their XML nodes
        functionalElementsFromPreviousInterim.forEach((f) => {
            let xmlId = '';
            switch (f.type) {
                // all  of the following functional elements share a nameXmlId attribute, so we treat them the same here
                case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER:
                case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE:
                    xmlId = (f as Signature).nameXmlId;
                    break;
                case FUNCTIONAL_ELEMENT_TYPE.DATE_PERFORMED: {
                    const datePerformed = f as DatePerformed;
                    if (!isDefined(datePerformed.dayXmlId)) {
                        throw new Error(
                            `AstoriaXmlUtilities: removePreviousInterimContent: missing dayXmlId id for ${datePerformed.type} Functional element type !`,
                        );
                    }

                    xmlId = datePerformed.dayXmlId;
                    break;
                }
                // all  of the following functional elements share a xmlId attribute, so we treat them the same here
                case FUNCTIONAL_ELEMENT_TYPE.DISCREPANCY_LOG:
                case FUNCTIONAL_ELEMENT_TYPE.VALUE:
                case FUNCTIONAL_ELEMENT_TYPE.VALUE_UNIT:
                    xmlId = (f as DiscrepancyLog).xmlId;
                    break;
                case FUNCTIONAL_ELEMENT_TYPE.OPTION:
                    xmlId = (f as Option).xmlId;
                    break;
                // WORK STEPS_PERFORMED does not contain any input fields processed by PACE Forms and as such can never
                // be m arked as "loaded from interim XML", so there is nothing to do here. The clause just exists for
                // comp leteness sake. A WORKSTEPS_PERFORMED will be removed when the opening OPTION is marked for
                // remo val as well, though.
                case FUNCTIONAL_ELEMENT_TYPE.WORKSTEPS_PERFORMED:
                    break;
                // Thes e signatures are hidden in the prologue for 832 and 834 forms and will never be part of an
                // inte rim training checklist, so there is no need to remove them
                case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE_SYSTEM_CONDITION:
                case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER_SYSTEM_CONDITION:
                    break;
                // all  of the following functional elements share a materialXmlId attribute, so we treat them the same here
                case FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO:
                case FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO_WITH_DESC:
                    xmlId = (f as MaterialSerialNumber).materialXmlId;
                    break;
                case FUNCTIONAL_ELEMENT_TYPE.LAST_PROCEDURE_DATE:
                case FUNCTIONAL_ELEMENT_TYPE.REPLACEMENT_DATE:
                    xmlId = (f as ReplacementOrLastProcedureDate).radioButtonXmlId;
                    break;
                case FUNCTIONAL_ELEMENT_TYPE.STANDARD:
                case FUNCTIONAL_ELEMENT_TYPE.REMARK:
                case FUNCTIONAL_ELEMENT_TYPE.DATE:
                case FUNCTIONAL_ELEMENT_TYPE.COMPONENT:
                case FUNCTIONAL_ELEMENT_TYPE.DEFECTIVE_COMPONENT_REPLACEMENT:
                case FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE:
                case FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE:
                case FUNCTIONAL_ELEMENT_TYPE.OPTION_PRESENT:
                case FUNCTIONAL_ELEMENT_TYPE.DROP_DOWN_OPTIONS:
                    throw new Error(
                        `AstoriaXmlUtilities: removePreviousInterimContent: Functional element type '${f.type}' not supported for Astoria!`,
                    );

                default:
                    assertUnreachable(f.type);
            }

            // if t he functional element is an OPTION, we need to remove its closing WORKSTEPS_PERFORMED as well
            if (isAstoriaOption(f)) {
                // Expl anation for XPath:
                // 1. F ind all PDFFORM elements in the XML document with the right TITLE attribute and pick the first one
                // 2. G o the hierarchy up until you arrive at the LOP-ENTRY ancestor
                // 3. G et a list of all following TABLE elements with a matching LOP-TABLE-ID attribute of
                //    ' WORKSTEPS_PERFORMED' and pick the first one
                // 4. G o the hierarchy up until your arrive at the LOP-ENTRY ancestor
                const workstepsPerformedPath = `//PDFFORM[@TITLE='${f.xmlId}'][1]//ancestor::LOP-ENTRY/following::TABLE[@LOP-TABLE-ID='WORKSTEPS_PERFORMED'][1]//ancestor::LOP-ENTRY`;
                const workstepsPerformedNode = getFirstNode(doc, doc, workstepsPerformedPath);

                if (workstepsPerformedNode !== null && workstepsPerformedNode.parentElement !== null) {
                    workstepsPerformedNode.parentElement.removeChild(workstepsPerformedNode);
                }
            }

            const functionalElementPath = `//PDFFORM[@TITLE='${xmlId}']//ancestor::LOP-ENTRY`;
            const functionalElementNode = getFirstNode(doc, doc, functionalElementPath);

            if (functionalElementNode !== null && functionalElementNode.parentElement !== null) {
                functionalElementNode.parentElement.removeChild(functionalElementNode);
            }
        });
    }

    public static removeByAnswer(doc: XMLDocument, options: RadioOptions): void {
        // Sign atures and their controlling checkpoints will never be removed
        const checkpointXmlIdsForSignatures: string[] = [];

        // Loop over all functional elements (=LOP tables) and check they have a controlling checkpoint answered with "NO" or "NA"
        const functionalElements = getChildNodes(doc, doc, '//TABLE[@LOP-TABLE-ID]');
        functionalElements.forEach((f) => {
            // Try  to find controlling (=directly preceding) checkpoint which is answered
            // f is a TABLE node. First parent is the LOP-TABLE node, second parent is the LOP-ENTRY node, then we can
            // get  the preceding sibling, which is the LOP-ENTRY of the controlling checkpoint. Descend to the PDFFORM
            // to f ind out what options is currently selected.
            const checkpointPath = `./parent::LOP-TABLE/parent::LOP-ENTRY/preceding-sibling::*[1][self::LOP-ENTRY]/CHECKBTN/PDFFORM[@TITLE]`;
            const optionNode = getFirstNode(doc, f, checkpointPath);
            const checkpointXmlId = optionNode?.getAttribute('TITLE');
            if (!isDefined(checkpointXmlId)) {
                // with out controlling checkpoint, there is nothing to remove
                return;
            }

            const elementType = f.getAttribute('LOP-TABLE-ID') ?? '';
            if (
                (elementType === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER ||
                    elementType === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE) &&
                optionNode !== null
            ) {
                checkpointXmlIdsForSignatures.push(checkpointXmlId);
                return;
            }

            const optionsForXmlId = options[checkpointXmlId];
            if (typeof optionsForXmlId === 'undefined') {
                throw new Error(
                    `AstoriaXmlUtilities: removeByAnswer: could not find options for xmlId '${checkpointXmlId}'`,
                );
            }
            const selectedOptionInMaster = optionsForXmlId.find((o) => o.selectedInMaster);
            const emptyOrNoOrNaInMaster =
                typeof selectedOptionInMaster === 'undefined' ||
                selectedOptionInMaster.optionType === RADIO_OPTION_TYPE.NO ||
                selectedOptionInMaster.optionType === RADIO_OPTION_TYPE.NA;

            // chec k if controlling checkpoint is answered with "No" and remove functional element if so. If there is no
            // cont rolling checkpoint, then optionNode will be null and we don't remove anything.
            if (
                optionNode !== null &&
                emptyOrNoOrNaInMaster && // only remove functionalElement if it was answered with "No" or "N.A." in the master
                f.parentElement !== null && // f.pa rent === LOP-TABLE
                f.parentElement.parentElement !== null && // f.pa rent.parent = LOP-ENTRY
                f.parentElement.parentElement.parentElement !== null // f.pa rent.parent.parent = LOP-CONTENT
            ) {
                f.parentElement.parentElement.parentElement.removeChild(f.parentElement.parentElement);
            }
        });

        // Loop over all checkpoints and check if they are answered with "Yes"
        const checkpoints = getChildNodes(doc, doc, '//CHECKPNT');
        checkpoints.forEach((c) => {
            const optionPath = './following-sibling::*[1][self::CHECKBTN]/PDFFORM[@TITLE]';
            const optionNode = getFirstNode(doc, c, optionPath);
            if (!isDefined(optionNode)) {
                throw new Error(
                    `AstoriaXmlUtilities: removeByAnswer: Could not find PDFFORM with TITLE for CHECKPNT element with content '${c.textContent}'`,
                );
            }

            const checkpointXmlId = optionNode.getAttribute('TITLE');
            if (!isDefined(checkpointXmlId)) {
                throw new Error(`AstoriaXmlUtilities: removeByAnswer: found checkpoint option without TITLE!`);
            }

            const checkpointOptions = options[checkpointXmlId];
            if (typeof checkpointOptions === 'undefined') {
                throw new Error(
                    `AstoriaXmlUtilities: removeByAnswer: Could not find options for checkpoint '${checkpointXmlId}'`,
                );
            }

            const selectedOptionInMaster = checkpointOptions.find((o) => o.selectedInMaster);
            const emptyOrNoOrNaInMaster =
                typeof selectedOptionInMaster === 'undefined' ||
                selectedOptionInMaster.optionType === RADIO_OPTION_TYPE.NO ||
                selectedOptionInMaster.optionType === RADIO_OPTION_TYPE.NA;

            // remo ve checkpoints that are empty or answered with NO or NA in the master
            if (
                emptyOrNoOrNaInMaster &&
                !checkpointXmlIdsForSignatures.includes(checkpointXmlId) &&
                c.parentElement !== null && // c.pa rent === LOP-ENTRY
                c.parentElement.parentElement !== null // c.pa rent.parent === LOP-CONTENT
            ) {
                c.parentElement.parentElement.removeChild(c.parentElement);
            }
        });
    }

    /**
     * Removes LOP-CONTENT tags that do not have any PDFFORM elements left in them to clean up the XML structure for
     * the PDF generator. Empty sections may occur after removing functional elements/checkpoints that are not answered
     * with "YES" in the previous cleanup steps.
     *
     * @param doc the XML document used to remove the empty sections
     */
    public static removeEmptySections(doc: XMLDocument): void {
        const emptySectionPath = '//LOP-CONTENT[not(descendant::*[self::PDFFORM])]';
        const emptySectionNodes = getChildNodes(doc, doc, emptySectionPath);

        emptySectionNodes.forEach((s) => {
            if (s.parentElement !== null) {
                s.parentElement.removeChild(s);
            }
        });
    }

    /**
     * Removes the second and third from the OPTION functional element structure to get rid of signature, name and date
     * input fields
     *
     * @param doc the XML document to be modified
     */
    public static removeSignatureFromOptionFunctionalElement(doc: XMLDocument): void {
        const optionPath = `//TABLE[@LOP-TABLE-ID='${FUNCTIONAL_ELEMENT_TYPE.OPTION}']`;
        const optionNodes = getChildNodes(doc, doc, optionPath);

        optionNodes.forEach((o) => {
            const rowPath = `.//ROW`;
            const rowNodes = getChildNodes(doc, o, rowPath);
            const expectedNumberOfRows = 3;

            // if t he signatures were already removed earlier, there is nothing left for us to do
            // in t his case we expect that one row exists - it contains the checkpoints and label for the functional element
            if (rowNodes.length === 1) {
                return;
            }

            if (rowNodes.length !== expectedNumberOfRows) {
                throw new Error(
                    `AstoriaXmlUtilities: removeSignatureFromOptionFunctionalElement: Encountered OPTION with unexpected number of rows in table body (found ${rowNodes.length}, expected ${expectedNumberOfRows})`,
                );
            }

            // the  first row contains the yes/no radio buttons and needs to stay
            const rowsToRemove = rowNodes.filter((r, index) => index > 0);
            rowsToRemove.forEach((r) => r.parentElement?.removeChild(r));
        });
    }

    /**
     * Removes the second and third from the WORKSTEPS_PERFORMED functional element structure to get rid of signature,
     * name and date input fields
     *
     * @param doc the XML document to be modified
     */
    public static removeSignatureFromWorkstepsPerformed(doc: XMLDocument): void {
        const workstepsPerformedPath = `//TABLE[@LOP-TABLE-ID='${FUNCTIONAL_ELEMENT_TYPE.WORKSTEPS_PERFORMED}']`;
        const workstepsPerformedNodes = getChildNodes(doc, doc, workstepsPerformedPath);

        workstepsPerformedNodes.forEach((w) => {
            const rowPath = `.//ROW`;
            const rowNodes = getChildNodes(doc, w, rowPath);
            const expectedNumberOfRows = 3;

            // if t he signatures were already remove earlier, there is nothing left for us to do
            if (rowNodes.length === 0) {
                return;
            }

            if (rowNodes.length !== expectedNumberOfRows) {
                throw new Error(
                    `AstoriaXmlUtilities: removeSignatureFromWorkstepsPerformed: Encountered WORKSTEPS_PERFORMED with unexpected number of rows in table body (found ${rowNodes.length}, expected ${expectedNumberOfRows})`,
                );
            }

            rowNodes.forEach((r) => r.parentElement?.removeChild(r));
        });
    }

    public static updateOptionDependencies(
        doc: XMLDocument,
        selected: OPTION_FUNCTIONAL_ELEMENT_TYPE.YES | OPTION_FUNCTIONAL_ELEMENT_TYPE.NO | undefined,
        dependencies: {
            checkpointOptions: CheckpointSelection[];
            functionalElements: FunctionalElement[];
        },
    ): void {
        // Upda te all dependent checkpoints in the XML as well
        const selectedInDependency = selected === OPTION_FUNCTIONAL_ELEMENT_TYPE.NO ? RADIO_OPTION_TYPE.NA : undefined;

        // cond ition1: if the dependent checkpoint are not selected, then update the functional element
        // cond ition2: in case the checkpoint are selected then updateCheckpoint take cares of updating functional element, so we do not update them again
        // cond ition3: if N.A is selected then checkpoint xml should be updated, reason updateCheckpoint is not fired
        if (
            selectedInDependency === RADIO_OPTION_TYPE.NA ||
            !isDefined(selectedInDependency) ||
            dependencies.checkpointOptions.some((checkpointOption) => checkpointOption?.isSelected)
        ) {
            // Upda te all dependent checkpoint element in the XML
            dependencies.checkpointOptions.forEach((c) => {
                AstoriaXmlUtilities.setCheckbtnByXmlId(doc, c.checkpoint.xmlId, selectedInDependency);
            });

            // Upda te all dependent functional element input fields in the XML
            // valu e should be reset if selectedInDependency is undefined
            dependencies.functionalElements
                .map((f) => getXmlIdsFromFunctionalElement(f))
                .reduce((accu, curr) => [...accu, ...curr], []) // flat ten the returned xmlId arrays
                .forEach(({ xmlId, value }) => {
                    if (xmlId) {
                        AstoriaXmlUtilities.setPdfFormByXmlId(doc, xmlId, isDefined(selectedInDependency) ? value : '');
                    }
                });
        }
    }

    public static getCustomerIdentification(doc: XMLDocument): CustomerIdentification {
        return {
            customer1: getCustomerIdentificationFieldByXmlId(doc, 'Customer'),
            customer2: getCustomerIdentificationFieldByXmlId(doc, 'a1'),
            address1: getCustomerIdentificationFieldByXmlId(doc, 'a2'),
            address2: getCustomerIdentificationFieldByXmlId(doc, 'a3'),
            communication1: getCustomerIdentificationFieldByXmlId(doc, 'Com1'),
            communication2: getCustomerIdentificationFieldByXmlId(doc, 'Com2'),
            department: getCustomerIdentificationFieldByXmlId(doc, 'Department'),
            materialNr: getCustomerIdentificationFieldByXmlId(doc, 'IVK-Material-No'),
            orderNr: getCustomerIdentificationFieldByXmlId(doc, 'CustomerData.OrderNo'),
            mDate: getCustomerIdentificationFieldByXmlId(doc, 'CustomerData.MDate'),
            room: getCustomerIdentificationFieldByXmlId(doc, 'Room'),
            serialNr: getCustomerIdentificationFieldByXmlId(doc, 'SerialNo'),
            systemId: getCustomerIdentificationFieldByXmlId(doc, 'SystemID'),
        };
    }
}

export const testableAstoriaXmlUtilitiesFunctions = {
    addAdditionalEntityToDoctype,
    getOrgData,
    getCopyright,
    getSystemCondition,
    getContentBlock,
    getSignature,
    getOption,
    getValueFunctionalElement,
    getMaterialSerialNumber,
    getSystemConditionMeasurements,
    getDatePerformed,
    getReplacementOrLastProcedureDate,
};
